import { PlcBrand } from './types/index.js';
export function resolvePlcTargetCapabilities(target) {
    const vendor = target.brand === PlcBrand.Siemens ? 'siemens'
        : target.brand === PlcBrand.Codesys ? 'codesys'
            : target.brand === PlcBrand.Beckhoff ? 'beckhoff' : 'generic';
    const version = Number(String(target.softwareVersion || '').match(/(?:V|v)?\s*(\d+)/)?.[1] || 0);
    const programmingModel = target.programmingModel || 'classic_plc';
    const series = `${target.hardwareSeries || ''} ${target.plcModel || ''}`.toUpperCase();
    return {
        vendor,
        programmingModel,
        sourceEncoding: target.sourceEncoding || 'utf8_bom',
        supportsNamedValueTypes: vendor === 'siemens'
            && /S7[- ]?1500|15\d\d/.test(series)
            && version >= 19
            && programmingModel === 'software_unit',
    };
}
const BLOCKS = [
    { start: /^\s*FUNCTION_BLOCK\b/i, end: /^\s*END_FUNCTION_BLOCK\b/i, kind: 'function_block' },
    { start: /^\s*ORGANIZATION_BLOCK\b/i, end: /^\s*END_ORGANIZATION_BLOCK\b/i, kind: 'organization_block' },
    { start: /^\s*DATA_BLOCK\b/i, end: /^\s*END_DATA_BLOCK\b/i, kind: 'data_block' },
    { start: /^\s*FUNCTION\b/i, end: /^\s*END_FUNCTION\b/i, kind: 'function' },
    { start: /^\s*PROGRAM\b/i, end: /^\s*END_PROGRAM\b/i, kind: 'program' },
    { start: /^\s*TYPE\b/i, end: /^\s*END_TYPE\b/i, kind: 'udt' },
    { start: /^\s*VAR_GLOBAL\b/i, end: /^\s*END_VAR\b/i, kind: 'global' },
];
function declaredName(line, kind) {
    const quoted = line.match(/["']([^"']+)["']/)?.[1];
    if (quoted)
        return quoted;
    const keyword = kind === 'function_block' ? 'FUNCTION_BLOCK'
        : kind === 'organization_block' ? 'ORGANIZATION_BLOCK'
            : kind === 'data_block' ? 'DATA_BLOCK'
                : kind === 'udt' ? 'TYPE' : kind.toUpperCase();
    return line.match(new RegExp(`^\\s*${keyword}\\s+([^\\s:;]+)`, 'i'))?.[1] || 'block';
}
/** 已经守 `GVL_` 约定的标识符。只认这一种写法，不从散文里猜名字。 */
const GVL_NAME_HINT = /\bGVL_[A-Za-z0-9_]+/;
function globalListModifierName(header) {
    if (/\bCONSTANT\b/i.test(header))
        return 'GVL_Constants';
    if (/\bPERSISTENT\b/i.test(header))
        return 'GVL_Persistent';
    if (/\bRETAIN\b/i.test(header))
        return 'GVL_Retain';
    return 'GVL_RealPLC';
}
/**
 * 一个全局变量表叫什么 —— 全仓只有这一处说了算。
 *
 * `VAR_GLOBAL` 在 ST 里没有名字槽，名字是工具替它取的；而工程师在 CODESYS 项目树里
 * 看到的就是这个名字，下载文件集里的 `GVLs/<名字>.st` 也是，两边本该对得上。此前两处
 * 各取各的：网关按 artifact 词干取（2026-09-21 线上那次，桌面工程叫 V1_0_Constants），
 * 这里按一个可选组会回溯的正则取，于是 `VAR_GLOBAL CONSTANT` 叫 CONSTANT、
 * `VAR_GLOBAL RETAIN` 叫 RETAIN、其余一律 GlobalTags —— 两个普通块因此撞进同一个文件。
 *
 * 规则按顺序：
 * 1. 块行上、或紧挨着块上方的注释里，已经守 `GVL_` 约定的标识符说了算；
 * 2. 否则看块修饰词：CONSTANT / PERSISTENT（带不带 RETAIN 都算）/ RETAIN；
 * 3. 否则 `GVL_RealPLC`；
 * 4. 结果同名时，先来的保留，后来的顺次 `_2`、`_3`（大小写不敏感，也要避开同一份产物
 *    里已经用掉的其它对象名）。
 */
export function globalVariableListName(input) {
    // 离块最近的那行注释最具体，所以从下往上找。
    const nearestComment = [...(input.comments ?? [])].reverse()
        .map(line => GVL_NAME_HINT.exec(line)?.[0])
        .find(Boolean);
    const base = GVL_NAME_HINT.exec(input.header)?.[0]
        ?? nearestComment
        ?? globalListModifierName(input.header);
    const used = new Set();
    for (const name of input.taken ?? [])
        used.add(name.toLowerCase());
    let name = base;
    for (let suffix = 2; used.has(name.toLowerCase()); suffix += 1)
        name = `${base}_${suffix}`;
    return name;
}
/** 注释行，以及紧挨着对象上方、本就属于那个对象的 pragma。 */
function commentAboveKind(line) {
    const trimmed = line.trim();
    if (trimmed.startsWith('//') || (trimmed.startsWith('(*') && trimmed.endsWith('*)')))
        return 'comment';
    return /^\{.*\}$/.test(trimmed) ? 'pragma' : 'other';
}
function safeFileName(value) {
    return value.trim().replace(/[\\/:*?"<>|\x00-\x1F]/g, '_').replace(/^\.+/, '') || 'block';
}
function classifyType(source) {
    return (!/\bSTRUCT\b/i.test(source) && /\([\s\S]*\)/.test(source)) || /\bENUM\b/i.test(source) ? 'enum' : 'udt';
}
/**
 * Where a CODESYS object lives in the delivered file set.
 *
 * The same three groups the project itself is built from — `codesysArtifactContracts`
 * splits the source into POU / GVL / DUT objects before handing it to the IDE, so the
 * `.project` the engineer opens already has them apart. Only the downloadable copy was
 * a flat pile of `.st`, which is what made an ENUM indistinguishable from a global list.
 *
 * The extension stays `.st` for every group: the directory already carries the
 * distinction, while changing the extension would travel into download names, the
 * markdown fence language and anything that re-imports by suffix.
 */
function codesysLocation(kind, safe) {
    const group = kind === 'global' ? 'GVLs'
        : kind === 'udt' || kind === 'enum' || kind === 'nvt' ? 'DUTs'
            : 'POUs';
    return { extension: '.st', relativePath: `${group}/${safe}.st` };
}
// ── Ladder blocks ────────────────────────────────────────────────
//
// A ladder block is written as ladder text inside the program, with its
// language on the header line: `FUNCTION_BLOCK "FB_Conveyor" LANGUAGE LAD`.
// The server renders it as SimaticML for TIA Portal, so it is never an SCL
// source file. Siemens is the only platform with that path.
// Design: docs/superpowers/specs/2026-09-23-tia-ladder-design.md.
const LADDER_BLOCK_HEADER = /^\s*(?:FUNCTION_BLOCK|ORGANIZATION_BLOCK)\s+(?:"[^"\n]+"|[A-Za-z_][A-Za-z0-9_]*)\s+LANGUAGE\s+LAD\b/i;
/** Whether this line opens a ladder block. */
export function isLadderBlockHeader(line) {
    return LADDER_BLOCK_HEADER.test(line);
}
/**
 * Whether the program holds at least one ladder block. A header inside a
 * (* … *) comment is not one — lad-core's listLadBlocks reads it the same way,
 * and a test holds the two together: a commented-out old ladder block must not
 * send an SCL program down the ladder path (file name, fence, checks, TIA).
 */
export function containsLadderBlocks(code) {
    if (typeof code !== 'string')
        return false;
    let inComment = false;
    for (const line of code.split(/\r?\n/)) {
        if (!inComment && isLadderBlockHeader(line))
            return true;
        let scan = line;
        while (scan.length > 0) {
            const next = scan.indexOf(inComment ? '*)' : '(*');
            if (next < 0)
                break;
            inComment = !inComment;
            scan = scan.slice(next + 2);
        }
    }
    return false;
}
function isSiemensVendor(vendor) {
    if (vendor === PlcBrand.Siemens)
        return true;
    const value = String(vendor ?? '').toLowerCase();
    return value.includes('siemens') || value === 's7' || value === 'tia';
}
/**
 * The extension of a whole-program file. The browser and the runtime both name
 * the program with this, so the file downloaded and the artifact filed agree.
 *
 * What decides is the content, not the language picked for the turn: until
 * ladder generation lands, a turn that picked ladder still produced SCL, and
 * the browser named it `.lad.txt` — which also sent it past the SCL encoding.
 * `vendor` is a PlcBrand or a vendor slug (`siemens`, `s7`, `tia`); anything
 * else is structured text.
 */
export function programFileExtension(input) {
    if (!isSiemensVendor(input.vendor))
        return '.st';
    return containsLadderBlocks(input.code) ? '.lad' : '.scl';
}
function objectLocation(kind, name, capabilities, ladder) {
    const safe = safeFileName(name);
    if (capabilities.vendor === 'codesys')
        return codesysLocation(kind, safe);
    if (capabilities.vendor !== 'siemens')
        return { extension: '.st', relativePath: `${safe}.st` };
    if (ladder)
        return { extension: '.lad', relativePath: `ProgramBlocks/${safe}.lad` };
    if (kind === 'data_block')
        return { extension: '.db', relativePath: `DataBlocks/${safe}.db` };
    if (kind === 'udt')
        return { extension: '.udt', relativePath: `Types/${safe}.udt` };
    if (kind === 'enum' || kind === 'nvt') {
        if (capabilities.supportsNamedValueTypes)
            return { extension: '.nvt', relativePath: `Types/${safe}.nvt`, targetCapability: 'siemens_s7_1500_tia_v19_software_unit' };
        return { extension: '.scl', relativePath: `Types/${safe}.scl`, targetCapability: 'named_constants_fallback_required' };
    }
    return { extension: '.scl', relativePath: `ProgramBlocks/${safe}.scl` };
}
export function extractPlcObjectDescriptors(code, target) {
    const lines = code.split(/\r?\n/);
    const capabilities = resolvePlcTargetCapabilities(target);
    const objects = [];
    let active;
    let startLine = '';
    let body = [];
    // 命名提示写在块上方的注释行里，而那些行本身不属于任何块，扫描时本来就跳过了。
    let commentsAbove = [];
    let headerComments = [];
    // 网关取名前先把整份产物里的显式名全部占上；这里若只看已经扫过的部分，一个在后面
    // 声明的同名对象会让同一份源码在项目树和下载列表里叫出两个名字。
    const taken = new Set(lines.flatMap(line => {
        const block = BLOCKS.find(candidate => candidate.start.test(line));
        return block && block.kind !== 'global' ? [declaredName(line, block.kind).toLowerCase()] : [];
    }));
    for (const line of lines) {
        if (!active) {
            active = BLOCKS.find(candidate => candidate.start.test(line));
            if (!active) {
                const above = commentAboveKind(line);
                if (above === 'comment')
                    commentsAbove.push(line);
                else if (above === 'other')
                    commentsAbove = [];
                continue;
            }
            startLine = line;
            headerComments = commentsAbove;
            commentsAbove = [];
            body = [line];
        }
        else {
            body.push(line);
        }
        if (!active.end.test(line))
            continue;
        const source = body.join('\n');
        const kind = active.kind === 'udt' ? classifyType(source) : active.kind;
        const name = active.kind === 'global'
            ? globalVariableListName({ header: startLine, comments: headerComments, taken })
            : declaredName(startLine, active.kind);
        taken.add(name.toLowerCase());
        objects.push({ declaredName: name, kind, source, dependencies: [], ...objectLocation(kind, name, capabilities, isLadderBlockHeader(startLine)) });
        active = undefined;
        startLine = '';
        headerComments = [];
        body = [];
    }
    return objects.map(object => ({
        ...object,
        dependencies: objects
            .filter(candidate => candidate !== object && object.source.includes(candidate.declaredName))
            .map(candidate => candidate.declaredName),
    }));
}
//# sourceMappingURL=plcObjects.js.map